import { PAGINATION_ITEM_TYPE } from './Pagination.constants'
import { PaginationHelper } from './Pagination.helper'
import type { usePaginationProps, usePaginationReturn } from './Pagination.types'

function usePagination(props: usePaginationProps): usePaginationReturn[] {
  const {
    boundaryCount = 1,
    count,
    disabled = false,
    onChange: handleChange,
    page,
    siblingCount = 1,
  } = props

  const paginationHelper = new PaginationHelper({ boundaryCount, count, page, siblingCount })

  function handleClick({ page }: { page: number | null }) {
    if (page) {
      handleChange({ page })
    }
  }

  const startPages = paginationHelper.getStartPages()
  const endPages = paginationHelper.getEndPages()

  const siblingsStart = paginationHelper.getSibilingsStart()
  const siblingsEnd = paginationHelper.getSibilingsEnd()

  // Basic list of items to render
  // e.g. itemList = ['previous', 1, 'ellipsis', 4, 5, 6, 'ellipsis', 10, 'next']
  const itemList = [
    PAGINATION_ITEM_TYPE.previous,
    ...startPages,

    // Start ellipsis
    ...paginationHelper.getStartEllipsis(),

    // Sibling pages
    ...paginationHelper.range(siblingsStart, siblingsEnd),

    // End ellipsis
    ...paginationHelper.getEndEllipsis(),

    ...endPages,
    PAGINATION_ITEM_TYPE.next,
  ]

  function getButtonPage(item: string | number): number | null {
    if (typeof item === 'number') {
      return item
    } else {
      switch (item) {
        case PAGINATION_ITEM_TYPE.previous:
          return page - 1
        case PAGINATION_ITEM_TYPE.next:
          return page + 1
        case PAGINATION_ITEM_TYPE.startEllipsis:
          return siblingsStart - 1
        case PAGINATION_ITEM_TYPE.endEllipsis:
          return siblingsEnd + 1
        default:
          return null
      }
    }
  }

  function isDisabled(item: string | number): boolean {
    if (item === PAGINATION_ITEM_TYPE.previous) return page <= 1
    if (item === PAGINATION_ITEM_TYPE.next) return page >= count

    return false
  }

  return itemList.map((item) => {
    const isNumber = typeof item === 'number'

    return {
      onClick: () => handleClick({ page: getButtonPage(item) }),
      itemType: isNumber ? PAGINATION_ITEM_TYPE.page : item.toString(),
      page: getButtonPage(item),
      disabled: disabled || isDisabled(item),
      ...(isNumber && { 'aria-current': item === page }),
    }
  })
}

export default usePagination
